import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, List } from 'lucide-react';
import { Checkbox, IconButton, Select } from '../../ui';
import { Icon } from '../../ui/icons';
import type { Filters, SortKey } from './model/listing';

export type View = 'grid' | 'list';
export type ListingPrefs = { view: View; sort: SortKey; filters: Filters };

/** Pages keep showing archived boards by default (as before P16-4); "Show archived" hides them. */
export const DEFAULT_PREFS: ListingPrefs = { view: 'grid', sort: 'edited', filters: { archived: true, templates: false, mine: false } };

function read(page: string): ListingPrefs {
  try {
    const raw = localStorage.getItem(`os.dashboard.${page}`);
    const v = raw ? (JSON.parse(raw) as Partial<ListingPrefs>) : {};
    return {
      view: v.view === 'list' ? 'list' : 'grid',
      sort: v.sort === 'name' ? 'name' : 'edited',
      filters: { ...DEFAULT_PREFS.filters, ...(typeof v.filters === 'object' && v.filters ? v.filters : {}) },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

/** View, sort and filters of one dashboard page, remembered in this browser (a convenience: it may be lost). */
export function useListingPrefs(page: string): [ListingPrefs, (next: ListingPrefs) => void] {
  const [prefs, setPrefs] = useState(() => read(page));
  const set = (next: ListingPrefs) => {
    setPrefs(next);
    try {
      localStorage.setItem(`os.dashboard.${page}`, JSON.stringify(next));
    } catch {
      // Private window or blocked storage: the choice lasts for this visit.
    }
  };
  return [prefs, set];
}

export function ListingBar({ prefs, onChange, mine = true }: { prefs: ListingPrefs; onChange: (next: ListingPrefs) => void; mine?: boolean }) {
  const { t } = useTranslation();
  const filter = (key: keyof Filters) => (on: boolean) => onChange({ ...prefs, filters: { ...prefs.filters, [key]: on } });
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="group" aria-label={t('dashboard.listing.label')}>
      <div className="flex items-center gap-0.5 rounded-full bg-surface-2 p-1">
        <IconButton label={t('dashboard.listing.grid')} pressed={prefs.view === 'grid'} icon={<Icon icon={LayoutGrid} size={16} />} onClick={() => onChange({ ...prefs, view: 'grid' })} />
        <IconButton label={t('dashboard.listing.list')} pressed={prefs.view === 'list'} icon={<Icon icon={List} size={16} />} onClick={() => onChange({ ...prefs, view: 'list' })} />
      </div>
      <label className="flex items-center gap-2 font-sans text-sm text-fg-muted">
        {t('dashboard.listing.sortBy')}
        <Select className="w-auto" value={prefs.sort} onChange={(e) => onChange({ ...prefs, sort: e.target.value as SortKey })}>
          <option value="edited">{t('dashboard.listing.edited')}</option>
          <option value="name">{t('dashboard.listing.name')}</option>
        </Select>
      </label>
      <Checkbox label={t('dashboard.listing.archived')} checked={prefs.filters.archived} onCheckedChange={filter('archived')} />
      {mine && <Checkbox label={t('dashboard.listing.mine')} checked={prefs.filters.mine} onCheckedChange={filter('mine')} />}
    </div>
  );
}
